import React, { PureComponent } from 'react';
import momentTimezone from 'moment-timezone';
import PropTypes from 'prop-types';

import { getFormattedDateTime, constants } from '@eb/datetime';
import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';
import { CheckChunky } from '@eb/eds-iconography';
import { EyeOnChunky } from '@eb/eds-iconography';
import { PublishChunky } from '@eb/eds-iconography';
import gettext from '@eb/gettext';

const { ISO_8601_FORMAT } = constants;

const EventHeaderButton = ({
    buttonText,
    dataAutomation,
    href,
    iconType,
    isShown,
    rel,
    target,
    buttonType = 'link',
    style = 'neutral',
}) => {
    let button = null;

    // NOTE: Have to declare butttons separately since Button will only style icon
    // correctly if it has a single icon child (not a span with an icon child, for example)
    if (isShown) {
        button = (
            <div className="" style={{ display: 'inline-flex' }}>
                <div className="eds-show-up-md l-pad-left-4 l-pad-bot-1">
                    <Button
                        data-automation={dataAutomation}
                        href={href}
                        rel={rel}
                        style={style}
                        target={target}
                        type={buttonType}
                    >
                        {buttonText}
                    </Button>
                </div>

                <div className="eds-show-down-mn l-pad-hor-2 l-pad-bot-1">
                    <Button
                        data-automation={dataAutomation}
                        href={href}
                        rel={rel}
                        style="neutral"
                        target={target}
                        type={buttonType}
                    >
                        <Icon type={iconType} />
                    </Button>
                </div>
            </div>
        );
    }

    return button;
};

export default class EventHeaderButtons extends PureComponent {
    static propTypes = {
        eventButtonsToShow: PropTypes.shape({
            isPreviewShown: PropTypes.bool.isRequired,
            isPublishShown: PropTypes.bool.isRequired,
            isViewShown: PropTypes.bool.isRequired,
        }).isRequired,
        manageHeaderAndNavUrls: PropTypes.shape({
            publishUrl: PropTypes.string.isRequired,
            viewUrl: PropTypes.string.isRequired,
        }).isRequired,
    };

    render() {
        const {
            changedDate,
            changedDateTz,
            locale,
            eventButtonsToShow,
            manageHeaderAndNavUrls,
        } = this.props;

        const changedDateWithTz = momentTimezone.tz(
            changedDate,
            ISO_8601_FORMAT,
            changedDateTz,
        );
        const browserTz = momentTimezone.tz.guess();
        const changedDateBrowserTz = getFormattedDateTime(
            changedDateWithTz,
            false,
            browserTz,
            locale,
            'L',
        );
        const changedTimeBrowserTz = getFormattedDateTime(
            changedDateWithTz,
            false,
            browserTz,
            locale,
            'HH:mm',
        );

        const {
            isPreviewShown,
            isPublishShown,
            isViewShown,
        } = eventButtonsToShow;

        const { previewUrl, publishUrl, viewUrl } = manageHeaderAndNavUrls;

        let lastSaved;

        if (isPublishShown) {
            lastSaved = (
                <p className="eds-text-bs eds-l-pad-right-2 eds-show-up-md eds-text--right">
                    {gettext(
                        'Last saved on %(changedDateBrowserTz)s at %(changedTimeBrowserTz)s',
                        { changedDateBrowserTz, changedTimeBrowserTz },
                    )}
                </p>
            );
        }

        const headerButtons = (
            <div>
                <div className="eds-align--right">
                    <EventHeaderButton
                        iconType={<CheckChunky />}
                        isShown={isPreviewShown}
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        buttonText={gettext('Preview')}
                    />
                    <EventHeaderButton
                        iconType={<EyeOnChunky />}
                        isShown={isViewShown}
                        href={viewUrl}
                        target="_view"
                        dataAutomation="view-event"
                        buttonText={gettext('View')}
                    />
                    <EventHeaderButton
                        iconType={<PublishChunky />}
                        isShown={isPublishShown}
                        style="fill"
                        href={publishUrl}
                        dataAutomation="make_event_live"
                        buttonText={gettext('Publish')}
                    />
                </div>
                {lastSaved}
            </div>
        );

        return (
            <div>
                <div className="eds-align--right eds-show-up-md">
                    {headerButtons}
                </div>
                <div className="eds-align--center eds-show-down-mn">
                    {headerButtons}
                </div>
            </div>
        );
    }
}
